/**
 * Environment Globals
 *
 * Lightweight module for environment singletons stored on globalThis.
 * No imports from env modules to avoid circular dependencies.
 *
 * Provides:
 * - Global key constants (single source of truth for names)
 * - Type-safe get/set/clear accessors
 * - Reset functions for test isolation
 *
 * Note: TypeScript type declarations for globals are in each env module
 * (server.ts, client.ts, payload.ts) to keep types colocated with their definitions.
 */

// =============================================================================
// GLOBAL KEY CONSTANTS
// =============================================================================

export const KEYS = {
    SERVER_ENV: '__SERVER_ENV__',
    SERVER_CONFIG: '__SERVER_CONFIG__',
    CLIENT_ENV: '__CLIENT_ENV__',
    CLIENT_CONFIG: '__CLIENT_CONFIG__',
    PAYLOAD_ENV: '__PAYLOAD_ENV__',
} as const

export type GlobalKey = (typeof KEYS)[keyof typeof KEYS]

// =============================================================================
// ACCESSORS
// =============================================================================

const globals = globalThis as Record<string, unknown>

/**
 * Get a value from globalThis.
 * @example getGlobal<ServerEnv>(KEYS.SERVER_ENV)
 */
export function getGlobal<T>(key: GlobalKey): T | undefined {
    return globals[key] as T | undefined
}

/**
 * Set a value on globalThis.
 * @example setGlobal(KEYS.SERVER_ENV, env)
 */
export function setGlobal<T>(key: GlobalKey, value: T): void {
    globals[key] = value
}

/**
 * Clear a value from globalThis.
 * @example clearGlobal(KEYS.SERVER_ENV)
 */
export function clearGlobal(key: GlobalKey): void {
    globals[key] = undefined
}

// =============================================================================
// RESET FUNCTIONS (for test isolation)
// =============================================================================

/**
 * Resets the ServerEnv singleton and config.
 */
export function resetServerEnv(): void {
    clearGlobal(KEYS.SERVER_ENV)
    clearGlobal(KEYS.SERVER_CONFIG)
}

/**
 * Resets the ClientEnv singleton and config.
 */
export function resetClientEnv(): void {
    clearGlobal(KEYS.CLIENT_ENV)
    clearGlobal(KEYS.CLIENT_CONFIG)
}

/**
 * Resets the PayloadEnv singleton.
 */
export function resetPayloadEnv(): void {
    clearGlobal(KEYS.PAYLOAD_ENV)
}

/**
 * Resets all environment singletons.
 * Convenience function for test setup.
 */
export function resetAllEnvs(): void {
    resetServerEnv()
    resetClientEnv()
    resetPayloadEnv()
}
